import { uiText } from "@/lib/i18n/uiText";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "@/lib/router-compat";
import { Check, Copy, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { formatLocalTime } from "@/lib/formatLocalTime";
import {
  AI_ACCESS_MONTHLY_CREDITS,
  AI_ACCESS_PRICE_LABEL,
  AI_ACCESS_PRICE_PERIOD,
  useAiAccess,
  useIssueMcpKey,
  useManageAiAccess,
  useStartAiAccessCheckout,
  useVerifyAiAccessCheckout,
} from "@/hooks/useAiAccess";

const INCLUDED = [
  "A secret MCP key for bots and scripts (60 requests/min)",
  "Every MCP tool: predictions, fixtures, form, odds and research endpoints",
  `${AI_ACCESS_MONTHLY_CREDITS} credits every billing month, shared across your account`,
  "Programmatic use for agents, assistants and automations",
];

/**
 * Paid AI-agent / MCP access — $25 per month, billed through Paystack.
 *
 * Entitlement is read from the database (`my_ai_access_status`), which only the
 * Paystack webhook writes, so nothing rendered here can grant access on its own.
 * The MCP key's plaintext is returned exactly once when it is issued or rotated.
 */
export function AiAccessSubscription() {
  const [params, setParams] = useSearchParams();
  const { toast } = useToast();
  const {
    active,
    status,
    current_period_end,
    cancel_at_period_end,
    keys,
    isSignedIn,
    isLoading,
  } = useAiAccess();
  const checkout = useStartAiAccessCheckout();
  const verify = useVerifyAiAccessCheckout();
  const manage = useManageAiAccess();
  const issue = useIssueMcpKey();
  const [freshKey, setFreshKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Paystack sends the member back with ?reference=… — confirm it server-side.
  const reference = params.get("reference") ?? params.get("trxref");
  useEffect(() => {
    if (!reference || !isSignedIn || verify.isPending || verify.isSuccess) return;
    verify.mutate(reference, {
      onSuccess: (data) => {
        toast({
          title: data.status === "active" ? "AI Access is live" : "Payment received",
          description: data.status === "active"
            ? "Issue your MCP key below, then call https://statlign.com/api/public/mcp with it."
            : "We're confirming the payment with Paystack — this page will update shortly.",
        });
        const next = new URLSearchParams(params);
        next.delete("reference");
        next.delete("trxref");
        setParams(next, { replace: true });
      },
      onError: (error: unknown) =>
        toast({
          title: "We couldn't confirm that payment",
          description: error instanceof Error ? error.message : "Please try again in a moment.",
          variant: "destructive",
        }),
    });
  }, [reference, isSignedIn, verify, toast, params, setParams]);

  const startCheckout = () =>
    checkout.mutate(undefined, {
      onError: (error: unknown) =>
        toast({
          title: "Checkout couldn't start",
          description: error instanceof Error ? error.message : "Please try again in a moment.",
          variant: "destructive",
        }),
    });

  const runManage = (action: "cancel" | "reactivate") =>
    manage.mutate(action, {
      onSuccess: () =>
        toast({
          title: action === "cancel" ? "Renewal cancelled" : "Subscription reactivated",
          description: action === "cancel"
            ? "Your key keeps working until the end of the current billing period."
            : "Your AI Access will renew as normal from now on.",
        }),
      onError: (error: unknown) =>
        toast({
          title: "That didn't go through",
          description: error instanceof Error ? error.message : "Please try again in a moment.",
          variant: "destructive",
        }),
    });

  const issueKey = () =>
    issue.mutate(undefined, {
      onSuccess: (data) => {
        setFreshKey(data.key);
        toast({
          title: "Key issued",
          description: "Copy it now — it is shown only once.",
        });
      },
      onError: (error: unknown) =>
        toast({
          title: "Could not issue a key",
          description: error instanceof Error ? error.message : "Please try again in a moment.",
          variant: "destructive",
        }),
    });

  const copyKey = async () => {
    if (!freshKey) return;
    await navigator.clipboard.writeText(freshKey);
    setCopied(true);
    toast({ title: "Key copied to clipboard" });
    setTimeout(() => setCopied(false), 1500);
  };

  const liveKeys = keys.filter((k) => k.active);

  return (
    <Card className="mb-6 border-primary/40">
      <CardHeader>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Badge className="gap-1">
            <ShieldCheck className="h-3 w-3" aria-hidden="true" />
             {uiText("Paid AI Access")} </Badge>
          {active && <Badge variant="secondary">{uiText("Active")}</Badge>}
        </div>
        <CardTitle className="text-lg">
          {AI_ACCESS_PRICE_LABEL}{" "}
          <span className="text-sm font-normal text-muted-foreground">
            {AI_ACCESS_PRICE_PERIOD}
          </span>
        </CardTitle>
        <CardDescription>
           {uiText("MCP access is not free. AI Access gives your bots and automations a secret key for statlign.com/api/public/mcp, and adds")} {AI_ACCESS_MONTHLY_CREDITS}  {uiText("credits to your account every month.")} </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <ul className="grid gap-2 text-sm sm:grid-cols-2">
          {INCLUDED.map((item) => (
            <li key={item} className="flex gap-2">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span className="text-muted-foreground">{item}</span>
            </li>
          ))}
        </ul>

        {isLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
             {uiText("Checking your subscription…")} </p>
        ) : !isSignedIn ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
               {uiText("Sign in first so we can attach the subscription and your MCP key to your account.")} </p>
            <Button asChild aria-label={uiText("Sign in to subscribe to Statlign AI Access")}>
              <Link to="/auth?next=/ai-access">{uiText("Sign in to continue")}</Link>
            </Button>
          </div>
        ) : active ? (
          <div className="space-y-3">
            {current_period_end && (
              <p className="text-xs text-muted-foreground">
                {cancel_at_period_end ? uiText("Access ends") : uiText("Renews")}  {uiText("on")}{" "}
                {formatLocalTime(current_period_end, "d MMM yyyy")}.
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={issueKey}
                disabled={issue.isPending}
                aria-label={liveKeys.length ? uiText("Rotate MCP API key") : uiText("Issue MCP API key")}
              >
                {issue.isPending
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  : <KeyRound className="mr-2 h-4 w-4" aria-hidden="true" />}
                {liveKeys.length ? uiText("Rotate my key") : uiText("Issue my MCP key")}
              </Button>
              {cancel_at_period_end ? (
                <Button
                  variant="outline"
                  onClick={() => runManage("reactivate")}
                  disabled={manage.isPending}
                  aria-label={uiText("Reactivate Statlign AI Access")}
                >
                  {manage.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  )}
                   {uiText("Reactivate")} </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => runManage("cancel")}
                  disabled={manage.isPending}
                  aria-label={uiText("Cancel Statlign AI Access renewal")}
                >
                  {manage.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  )}
                   {uiText("Cancel renewal")} </Button>
              )}
            </div>

            {freshKey && (
              <div className="space-y-2 rounded-lg border border-primary/40 bg-muted/40 p-3">
                <p className="text-xs font-medium">
                   {uiText("Copy this key now — it is shown once and stored only as a hash.")} </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 overflow-x-auto rounded bg-background px-2 py-1.5 text-xs">
                    {freshKey}
                  </code>
                  <Button size="sm" variant="ghost" onClick={copyKey} aria-label={uiText("Copy MCP API key")}>
                    {copied
                      ? <Check className="h-3.5 w-3.5" aria-hidden="true" />
                      : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                   {uiText("Send it as")} <code>{uiText("Authorization: Bearer <key>")}</code> {uiText("to https://statlign.com/api/public/mcp.")} </p>
              </div>
            )}

            {keys.length > 0 && (
              <ul className="divide-y rounded-lg border text-sm">
                {keys.map((k) => (
                  <li key={k.id} className="flex flex-wrap items-center justify-between gap-2 p-2.5">
                    <span className="text-muted-foreground">
                      {k.label ?? "MCP key"} · {k.rate_limit_per_min ?? 0}{uiText("/min")} </span>
                    <Badge variant={k.active ? "secondary" : "outline"}>
                      {k.active ? uiText("Active") : uiText("Retired")}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {status === "pending" && (
              <p className="text-xs text-muted-foreground">
                 {uiText("We're waiting on Paystack to confirm your last payment.")} </p>
            )}
            {status === "past_due" && (
              <p className="text-xs text-muted-foreground">
                 {uiText("Your last renewal didn't go through. Starting a new checkout restores access.")} </p>
            )}
            <Button
              onClick={startCheckout}
              disabled={checkout.isPending}
              aria-label={uiText("Subscribe to Statlign AI Access")}
            >
              {checkout.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              )}
               {uiText("Subscribe with Paystack")} </Button>
            <p className="text-xs text-muted-foreground">
               {uiText("Card, bank transfer, USSD, mobile money and QR supported. Cancel any time — access runs to the end of the period you paid for.")} </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
